import { Injectable, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs';
import { Category } from '@core/models';
import { StoreConfigService } from './store-config.service';
import { CategoryService } from './category.service';
import { ProductService } from './product.service';

/**
 * Qué hay publicado en la tienda actual, para decidir qué opciones de navegación
 * mostrar (header y footer). Una sola consulta por slug para los dos.
 */
@Injectable({ providedIn: 'root' })
export class CatalogNavService {
  private storeConfig = inject(StoreConfigService);
  private categoryService = inject(CategoryService);
  private productService = inject(ProductService);

  // Sin slug (directorio, cuenta) no hay tienda que consultar: evita GET /store//categories.
  private slug$ = toObservable(this.storeConfig.currentSlug).pipe(filter(Boolean));

  /** Categorías con productos publicados (se renueva al cambiar de tienda). */
  readonly categories = toSignal(this.slug$.pipe(switchMap(() => this.categoryService.getVisible())), {
    initialValue: [] as Category[],
  });
  readonly hasCategories = computed(() => this.categories().length > 0);

  /** `undefined` mientras carga: la opción aparece recién cuando se sabe que hay algo. */
  private typeCounts = toSignal(this.slug$.pipe(switchMap(() => this.productService.countByType())));

  hasType(type: 'PRODUCT' | 'SERVICE' | 'KIT'): boolean {
    const counts = this.typeCounts();
    if (!counts) return false;
    return counts[type] !== 0; // null = no se pudo contar → mostrar
  }
}
